import * as React from 'react';
import { useMemo, useState } from 'react';
import { Building2, Edit, MapPin, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { usePermissions } from '@klareospace/pharos-lib-front-utils';
import type { CustomerWithResolvedRefs, PhoneNumber, SortDirection } from '@klareospace/pharos-lib-types';
import { CUSTOMER_TYPE } from '@klareospace/pharos-lib-types';
import type { DataTableColumn, RowAction, SimplePagination } from '@klareospace/pharos-lib-web-utils';
import {
  Badge,
  createActionsColumn,
  DataTable,
  RowActionsMenu,
  TextCell,
  useDataTablePagination,
  useDataTableSorting,
} from '@klareospace/pharos-lib-web-utils';

import { useCustomersColumnOrder } from '../hooks';
import type { CustomerSortableColumn } from '../types/customer.types';
import { CustomerDrawer } from './customer-drawer';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface CustomersSorting {
  onSort: (sortBy: CustomerSortableColumn, sortDirection: SortDirection | null) => void;
  sortBy: CustomerSortableColumn | undefined;
  sortDirection: SortDirection | undefined;
}

export interface CustomersTableProps {
  customers: CustomerWithResolvedRefs[];
  isLoading: boolean;
  pagination: SimplePagination;
  sorting: CustomersSorting;
}

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_SITE_BADGES = 2;

// ─── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Formats a phone number for display, handling legacy records that may store
 * the dial code under `countryCode` instead of `dialCode`.
 */
function formatPhoneDisplay(phone: PhoneNumber): string {
  const raw = phone as unknown as Record<string, string>;
  const dialCode = phone.dialCode || raw['countryCode'] || '';
  return `${dialCode} ${phone.number}`.trim();
}

// ─── Component ────────────────────────────────────────────────────────────────

export function CustomersTable({ customers, isLoading, pagination, sorting }: CustomersTableProps): React.JSX.Element {
  const { t: tCommon } = useTranslation('common');
  const { t } = useTranslation('customers');
  const { hasPermission } = usePermissions();

  const [editingCustomer, setEditingCustomer] = useState<CustomerWithResolvedRefs | null>(null);
  const { columnOrder, onColumnOrderChange } = useCustomersColumnOrder();

  const getRowActions = useMemo(
    () =>
      (customer: CustomerWithResolvedRefs): RowAction[] => {
        const actions: RowAction[] = [
          {
            icon: <Edit size={16} />,
            label: t('actions.edit'),
            onClick: (): void => setEditingCustomer(customer),
            requiredPermission: 'customers:manage',
          },
        ];

        return actions.filter((action) => !action.requiredPermission || hasPermission(action.requiredPermission));
      },
    [hasPermission, t],
  );

  const columns = useMemo<DataTableColumn<CustomerWithResolvedRefs>[]>(
    () => [
      {
        cell: (customer): React.ReactNode => <NameCell customer={customer} />,
        className: 'w-[20%] min-w-[150px] font-normal',
        header: t('table.columns.name'),
        key: 'companyName',
        sortable: true,
      },
      {
        cell: (customer): React.ReactNode => (
          <div className="flex flex-col gap-0.5">
            <span className="truncate text-sm">{customer.email ?? '—'}</span>
            <span className="truncate text-sm text-muted-foreground">
              {customer.phone ? formatPhoneDisplay(customer.phone) : '—'}
            </span>
          </div>
        ),
        cellClassName: 'hidden sm:table-cell',
        className: 'hidden sm:table-cell w-[20%] min-w-[150px]',
        header: t('table.columns.contact'),
        key: 'contact',
      },
      {
        cell: (customer): React.ReactNode => <Badge variant="outline">{t(`form.typeOptions.${customer.type}`)}</Badge>,
        className: 'w-[10%] min-w-[90px]',
        header: t('table.columns.type'),
        key: 'type',
      },
      {
        cell: (customer): React.ReactNode => (
          <span className="flex items-center gap-1 text-sm text-muted-foreground">
            <MapPin className="h-3 w-3 shrink-0" />
            {customer.addresses.length}
          </span>
        ),
        cellClassName: 'hidden md:table-cell',
        className: 'hidden md:table-cell w-[10%] min-w-[80px]',
        header: t('table.columns.addresses'),
        key: 'addresses',
      },
      {
        cell: (customer): React.ReactNode => <LastOrderCell lastOrderDate={customer.lastOrderDate} />,
        className: 'w-[12%] min-w-[150px]',
        header: t('table.columns.lastOrder'),
        key: 'lastOrder',
      },
      {
        cell: (customer): React.ReactNode => <SitesBadges sites={customer.resolvedSites} />,
        className: 'w-[20%] min-w-[180px]',
        header: t('table.columns.sites'),
        key: 'sites',
      },
      createActionsColumn<CustomerWithResolvedRefs>((customer) => (
        <RowActionsMenu actions={getRowActions(customer)} ariaLabel={t('table.columns.actions')} />
      )),
    ],
    [getRowActions, t],
  );

  const paginationLabels = useMemo(
    () => ({
      nextPage: tCommon('pagination.nextPage'),
      pageLabel: (current: number, total: number): string => tCommon('pagination.pageLabel', { current, total }),
      pageSizeLabel: tCommon('pagination.rowsPerPage'),
      previousPage: tCommon('pagination.previousPage'),
      showingText: (start: number, end: number, total: number): string =>
        tCommon('pagination.showing', { end, start, total }),
    }),
    [tCommon],
  );

  const tablePagination = useDataTablePagination({ labels: paginationLabels, pagination });

  const tableSorting = useDataTableSorting<CustomerSortableColumn>({
    onSort: sorting.onSort,
    sortBy: sorting.sortBy,
    sortDirection: sorting.sortDirection,
  });

  return (
    <>
      <DataTable<CustomerWithResolvedRefs>
        ariaLabel={t('table.ariaLabel')}
        columnReorder={{ onOrderChange: onColumnOrderChange, order: columnOrder }}
        columns={columns}
        emptyMessage={t('emptyState')}
        isLoading={isLoading}
        pagination={tablePagination}
        rowKey={(customer): string => customer.id}
        rows={customers}
        sorting={tableSorting}
      />
      <CustomerDrawer
        customer={editingCustomer}
        onClose={(): void => setEditingCustomer(null)}
        open={editingCustomer !== null}
      />
    </>
  );
}

// ─── Private sub-components ───────────────────────────────────────────────────

interface NameCellProps {
  customer: CustomerWithResolvedRefs;
}

function NameCell({ customer }: NameCellProps): React.JSX.Element {
  const isCompany = customer.type === CUSTOMER_TYPE.COMPANY;
  return (
    <div className="flex items-center gap-2">
      <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
        {isCompany ? <Building2 className="h-4 w-4 text-primary" /> : <User className="h-4 w-4 text-primary" />}
      </div>
      <div className="flex min-w-0 flex-col">
        {isCompany ? (
          <>
            <span className="block truncate text-sm font-medium text-foreground">{customer.companyName ?? '—'}</span>
            <span className="truncate text-xs text-muted-foreground">
              {customer.firstName} {customer.lastName}
            </span>
          </>
        ) : (
          <span className="block truncate text-sm font-medium text-foreground">
            {customer.firstName} {customer.lastName}
          </span>
        )}
      </div>
    </div>
  );
}

interface LastOrderCellProps {
  lastOrderDate: string | undefined;
}

function LastOrderCell({ lastOrderDate }: LastOrderCellProps): React.JSX.Element {
  if (!lastOrderDate) {
    return <TextCell>—</TextCell>;
  }
  const date = new Date(lastOrderDate);
  const day = String(date.getUTCDate()).padStart(2, '0');
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const year = date.getUTCFullYear();
  return <span className="text-sm">{`${day}/${month}/${year}`}</span>;
}

interface SitesBadgesProps {
  sites: CustomerWithResolvedRefs['resolvedSites'];
}

function SitesBadges({ sites }: SitesBadgesProps): React.JSX.Element {
  const visible = sites.slice(0, MAX_SITE_BADGES);
  const overflowCount = sites.length - MAX_SITE_BADGES;

  return (
    <div className="flex flex-wrap items-center gap-1">
      {visible.map((site) => (
        <Badge className="max-w-[100px] truncate text-[10px]" key={site.id} variant="secondary">
          {site.name}
        </Badge>
      ))}
      {overflowCount > 0 && (
        <Badge className="text-[10px]" variant="outline">
          +{overflowCount}
        </Badge>
      )}
    </div>
  );
}
